import baseApi from './RootBaseApi';
import {
  CommunityUsersResponse,
  CommunityUsersQueryParams,
  UpdateUserStatusPayload,
  UpdateUserRolePayload,
  EligibleCommunityUser,
  CommunityInvitationsResponse,
  CommunityInvitationsQueryParams,
  AddCommunityUserPayload,
  CreateCommunityInvitationPayload,
  CommunityInvitation,
  CommunityInvitationResolution,
} from '@/types/community-user.types';

export const CommunityUserService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCommunityUsers: builder.query<
      CommunityUsersResponse,
      CommunityUsersQueryParams
    >({
      query: ({ community_id, ...params }) => ({
        url: `communities/${community_id}/users`,
        method: 'GET',
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
        },
      }),
      transformResponse: (response: CommunityUsersResponse) => response,
      providesTags: ['Users'],
    }),
    updateUserStatus: builder.mutation<void, UpdateUserStatusPayload>({
      query: ({ community_id, user_id, action }) => ({
        url: `communities/${community_id}/users/${user_id}`,
        method: 'DELETE',
        params: { action },
      }),
      invalidatesTags: ['Users'],
    }),
    updateUserRole: builder.mutation<void, UpdateUserRolePayload>({
      query: ({ community_id, user_id, role }) => ({
        url: `communities/${community_id}/users/${user_id}/role`,
        method: 'PATCH',
        data: { role: role },
      }),
      invalidatesTags: ['Users'],
    }),
    getEligibleCommunityUsers: builder.query<
      EligibleCommunityUser[],
      { community_id: string; search?: string; limit?: number }
    >({
      query: ({ community_id, search, limit }) => ({
        url: `communities/${community_id}/eligible-users`,
        method: 'GET',
        params: { search, limit },
      }),
      providesTags: ['Users'],
    }),
    addCommunityUser: builder.mutation<
      EligibleCommunityUser,
      AddCommunityUserPayload
    >({
      query: ({ community_id, user_id, role }) => ({
        url: `communities/${community_id}/users`,
        method: 'POST',
        data: { user_id, role },
      }),
      invalidatesTags: ['Users'],
    }),
    getCommunityInvitations: builder.query<
      CommunityInvitationsResponse,
      CommunityInvitationsQueryParams
    >({
      query: ({ community_id, ...params }) => ({
        url: `communities/${community_id}/invitations`,
        method: 'GET',
        params,
      }),
      providesTags: ['CommunityInvitations'],
    }),
    getCommunityInvitationResolution: builder.query<
      CommunityInvitationResolution,
      string
    >({
      query: (token) => ({
        url: `communities/invitations/${token}`,
        method: 'GET',
      }),
    }),
    acceptCommunityInvitation: builder.mutation<CommunityInvitation, string>({
      query: (token) => ({
        url: `communities/invitations/${token}/accept`,
        method: 'POST',
      }),
      invalidatesTags: ['CommunityInvitations', 'Users'],
    }),
    createCommunityInvitation: builder.mutation<
      CommunityInvitation,
      CreateCommunityInvitationPayload
    >({
      query: ({ community_id, email, role, expires_at }) => ({
        url: `communities/${community_id}/invitations`,
        method: 'POST',
        data: { email, role, expires_at },
      }),
      invalidatesTags: ['CommunityInvitations'],
    }),
    resendCommunityInvitation: builder.mutation<
      CommunityInvitation,
      { community_id: string; invitation_id: string }
    >({
      query: ({ community_id, invitation_id }) => ({
        url: `communities/${community_id}/invitations/${invitation_id}/resend`,
        method: 'POST',
      }),
      invalidatesTags: ['CommunityInvitations'],
    }),
    cancelCommunityInvitation: builder.mutation<
      CommunityInvitation,
      { community_id: string; invitation_id: string }
    >({
      query: ({ community_id, invitation_id }) => ({
        url: `communities/${community_id}/invitations/${invitation_id}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: ['CommunityInvitations'],
    }),
  }),
});

export const {
  useGetCommunityUsersQuery,
  useUpdateUserStatusMutation,
  useUpdateUserRoleMutation,
  useGetEligibleCommunityUsersQuery,
  useAddCommunityUserMutation,
  useGetCommunityInvitationsQuery,
  useGetCommunityInvitationResolutionQuery,
  useAcceptCommunityInvitationMutation,
  useCreateCommunityInvitationMutation,
  useResendCommunityInvitationMutation,
  useCancelCommunityInvitationMutation,
} = CommunityUserService;
